import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import {
  Alert,
  CheckIcon,
  CloseIcon,
  EyeClosedIcon,
  EyeIcon,
  GKButton,
  GKModal,
  GKOtpInput,
  GKTextField,
  Grid2,
  IconButton,
  Typography,
} from "components";
import { useMutation } from "hooks/useQueries";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useAppSelector } from "store/store";
import axiosInstance from "utils/axiosInstance";
import { GKModalProps } from "utils/commonInterface";
import * as Yup from "yup";

const ChangePasswordModal = ({ open, setOpen }: GKModalProps) => {
  const userData = useAppSelector((state) => state?.user?.userData);

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required("Password is required")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
        "Password must meet the requirements.",
      ),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm Password is required"),
  });

  const [otpCode, setOtpCode] = useState("");
  const [isDoneTyping, setIsDoneTyping] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [verifyResponse, setVerifyResponse] = useState<any>(null);

  const { mutate: handleGetOPT, isLoading } = useMutation({
    mutationFn: () =>
      axios.post(
        `${process.env.REACT_APP_BASE_URL}/otp/${userData?.countryCode}-${userData?.phone}/send_otp/`,
        {
          email: userData?.email,
        },
      ),
  });

  const { mutate: handleVerifyOPT } = useMutation({
    mutationFn: (otpCode: any) =>
      axios.post(
        `${process.env.REACT_APP_BASE_URL}/otp/${userData?.countryCode}-${userData?.phone}/login/`,
        {
          otp: otpCode,
        },
      ),
    onSuccess: (response) => {
      setVerifyResponse(response?.data);
      axiosInstance.interceptors.request.use((config) => {
        config.headers.Authorization = `Token ${response?.data?.token}`;
        return config;
      });
    },
  });

  const form = useForm({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    resolver: yupResolver(validationSchema),
  });

  form.watch();

  const onSubmit = async (values: any) => {
    await axiosInstance
      .post(`${process.env.REACT_APP_BASE_URL}/user/forgot_password/`, {
        phone: userData?.phone,
        password: values?.password,
        confirm_password: values?.password,
      })
      .then(() => {
        setOpen(false);
        toast.success("Password change successfully");
      })
      .catch((error: any) => {
        toast.error(
          error?.response?.data?.message ||
            "Something went wrong, please try again.",
        );
      });
  };

  useEffect(() => {
    if (isDoneTyping) handleVerifyOPT(otpCode);
  }, [isDoneTyping]);

  return (
    <GKModal
      open={open}
      setOpen={setOpen}
      modalTitle="Change Password"
      titleComponent={
        <GKButton
          variant="outlined"
          size="medium"
          loading={isLoading}
          onClick={handleGetOPT}
        >
          Resend OTP
        </GKButton>
      }
      footer={
        !verifyResponse ? (
          <GKButton
            color="dark"
            variant="outlined"
            onClick={() => setOpen(false)}
          >
            Close
          </GKButton>
        ) : (
          <GKButton
            type="submit"
            variant="contained"
            size="medium"
            disabled={!verifyResponse}
            form="OPT-form"
          >
            Change Password
          </GKButton>
        )
      }
    >
      <form onSubmit={form.handleSubmit(onSubmit)} id="OPT-form">
        <Grid2 container spacing={1}>
          {!verifyResponse && (
            <Grid2 size={12}>
              <Alert severity="warning" variant="filled">
                We have sent an OTP to your registered phone number. Please
                verify it to proceed further.
              </Alert>
            </Grid2>
          )}
          {!verifyResponse && (
            <Grid2 size={12}>
              <GKOtpInput
                value={otpCode}
                onChange={setOtpCode}
                setIsTyping={setIsDoneTyping}
              />
            </Grid2>
          )}
          {verifyResponse && (
            <Grid2 size={12}>
              <Controller
                name="password"
                control={form.control}
                render={({ field, formState: { errors, touchedFields } }) => (
                  <GKTextField
                    {...field}
                    inputLabel="Password"
                    type={showPassword ? "text" : "password"}
                    error={
                      Boolean(touchedFields.password) &&
                      Boolean(errors.password)
                    }
                    helperText={
                      Boolean(touchedFields.password) &&
                      errors.password?.message
                    }
                    slotProps={{
                      input: {
                        style: {
                          paddingRight: 12,
                        },
                        endAdornment: (
                          <IconButton
                            onClick={() => {
                              setShowPassword(!showPassword);
                            }}
                            style={{
                              padding: 0,
                            }}
                          >
                            {!showPassword ? <EyeIcon /> : <EyeClosedIcon />}
                          </IconButton>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid2>
          )}
          {verifyResponse && (
            <Grid2
              container
              size={12}
              mt={1}
              p={1}
              style={{
                border: "solid 1px",
                borderRadius: "10px",
                background: "var(--orange-400)",
                borderColor: "var(--orange-700)",
              }}
            >
              <Grid2 size={12}>
                <Typography
                  whiteSpace={"nowrap"}
                  fontSize={12}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    textDecoration: /[A-Z]/.test(form.getValues("password"))
                      ? "line-through"
                      : "",
                  }}
                >
                  {/[A-Z]/.test(form.getValues("password")) ? (
                    <CheckIcon
                      width={10}
                      height={10}
                      color={"var(--success-700)"}
                    />
                  ) : (
                    <CloseIcon
                      height={10}
                      width={10}
                      color={`var(--warning-700)`}
                    />
                  )}
                  Password should contain an Upper Case Letter
                </Typography>
                <Typography
                  fontSize={12}
                  whiteSpace={"nowrap"}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    textDecoration: /[a-z]/.test(form.getValues("password"))
                      ? "line-through"
                      : "",
                  }}
                >
                  {/[a-z]/.test(form.getValues("password")) ? (
                    <CheckIcon
                      width={10}
                      height={10}
                      color={"var(--success-700)"}
                    />
                  ) : (
                    <CloseIcon
                      height={10}
                      width={10}
                      color={`var(--warning-700)`}
                    />
                  )}
                  Password should contain a Lower Case Letter
                </Typography>
                <Typography
                  fontSize={12}
                  whiteSpace={"nowrap"}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    textDecoration: /[@$!%*?&_]/.test(
                      form.getValues("password"),
                    )
                      ? "line-through"
                      : "",
                  }}
                >
                  {/[@$!%*?&_]/.test(form.getValues("password")) ? (
                    <CheckIcon
                      width={10}
                      height={10}
                      color={"var(--success-700)"}
                    />
                  ) : (
                    <CloseIcon
                      height={10}
                      width={10}
                      color={`var(--warning-700)`}
                    />
                  )}
                  Password should contain a Special character
                </Typography>
                <Typography
                  fontSize={12}
                  whiteSpace={"nowrap"}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    textDecoration: /\d/.test(form.getValues("password"))
                      ? "line-through"
                      : "",
                  }}
                >
                  {/\d/.test(form.getValues("password")) ? (
                    <CheckIcon
                      width={10}
                      height={10}
                      color={"var(--success-700)"}
                    />
                  ) : (
                    <CloseIcon
                      height={10}
                      width={10}
                      color={`var(--warning-700)`}
                    />
                  )}
                  Password should contain a number
                </Typography>
                <Typography
                  fontSize={12}
                  whiteSpace={"nowrap"}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    textDecoration: /^([a-zA-Z0-9_*&^%$#@!]){8,}$/.test(
                      form.getValues("password"),
                    )
                      ? "line-through"
                      : "",
                  }}
                >
                  {/^([a-zA-Z0-9_*&^%$#@!]){8,}$/.test(
                    form.getValues("password"),
                  ) ? (
                    <CheckIcon
                      width={10}
                      height={10}
                      color={"var(--success-700)"}
                    />
                  ) : (
                    <CloseIcon
                      height={10}
                      width={10}
                      color={`var(--warning-700)`}
                    />
                  )}
                  Password should atleast contain minimum 8 Characters
                </Typography>
              </Grid2>
            </Grid2>
          )}
          {verifyResponse && (
            <Grid2 size={12}>
              <Controller
                name="confirmPassword"
                control={form.control}
                render={({ field, formState: { errors, touchedFields } }) => (
                  <GKTextField
                    {...field}
                    type={showConfirmPassword ? "text" : "password"}
                    inputLabel="Confirm Password"
                    placeholder="Confirm Password"
                    error={
                      Boolean(touchedFields.confirmPassword) &&
                      Boolean(errors.confirmPassword)
                    }
                    helperText={
                      Boolean(touchedFields.confirmPassword) &&
                      errors.confirmPassword?.message
                    }
                    slotProps={{
                      input: {
                        style: {
                          paddingRight: 12,
                        },
                        endAdornment: (
                          <IconButton
                            onClick={() => {
                              setShowConfirmPassword(!showConfirmPassword);
                            }}
                            style={{
                              padding: 0,
                            }}
                          >
                            {!showConfirmPassword ? (
                              <EyeIcon />
                            ) : (
                              <EyeClosedIcon />
                            )}
                          </IconButton>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid2>
          )}
        </Grid2>
      </form>
    </GKModal>
  );
};

export default ChangePasswordModal;
